import { cn } from 'util/cn'

import React, { useLayoutEffect, useRef } from 'react'
import { mergeRefs } from 'react-merge-refs'

import { css } from 'design'
import { Stack } from 'primitives/containers'

export type InputProps = {
  id: string
  label?: string
  dirty?: boolean
  description?: string
  error?: any
  disabled?: boolean
  required?: boolean
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  icon?: React.ReactNode
  action?: React.ReactNode
  type?: string
} & React.InputHTMLAttributes<HTMLInputElement>

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      description,
      dirty,
      error,
      disabled,
      required,
      onChange,
      className,
      icon,
      action,
      type = 'text',
      autoComplete = 'off',
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const actionContainerRef = useRef<HTMLSpanElement>(null)

    useLayoutEffect(() => {
      if (!actionContainerRef.current) {
        return
      }

      if (!inputRef.current) {
        return
      }

      const input = inputRef.current as HTMLInputElement
      input.style.paddingRight = `${
        actionContainerRef.current.offsetWidth + 8
      }px`
    }, [])

    const errorId = `${id}__error`
    const hasIcon = !!icon

    return (
      <Stack className={inputContainerStyles()} gap="1">
        <div className={inputContainerStyles()}>
          <input
            placeholder=" "
            {...props}
            type={type}
            id={id}
            className={cn(
              'topic__input',
              inputStyles({ hasIcon, hasAction: !!action }),
              className,
            )}
            onChange={onChange}
            aria-describedby={errorId}
            aria-invalid={!!error}
            disabled={disabled}
            required={required}
            ref={mergeRefs([ref, inputRef])}
            autoComplete={autoComplete}
          />
          <label
            className={labelStyles({ hasIcon })}
            id={`${id}__label`}
            htmlFor={id}
          >
            <span>{label}</span>
            {required && <span className={requiredLabelStyles()}>*</span>}
          </label>

          {/* The order here is important for CSS selectors */}
          {hasIcon && (
            <span className={iconStyles({ disabled })}>
              {icon}
            </span>
          )}

          {/* An action is usually a button */}
          {action && (
            <span ref={actionContainerRef} className={actionStyles()}>
              {action}
            </span>
          )}
        </div>

        {error && (
          <span
            id={errorId}
            role="alert"
            className={errorStyles()}
          >
            {error}
          </span>
        )}
        {description && (
          <span className={descriptionStyles()}>
            {description}
          </span>
        )}
      </Stack>
    )
  },
)

Input.displayName = 'Input'

const iconStyles = css({
  position: 'absolute',
  display: 'flex',
  flex: 'none',
  left: '$2',
  top: '50%',
  transform: 'translateY(-50%)',
  fill: '$grey600',

  variants: {
    disabled: {
      true: {
        opacity: 0.2,
      },
    },
  },
})

const actionStyles = css({
  position: 'absolute',
  display: 'flex',
  flex: 'none',
  right: '$2',
  top: '50%',
  transform: 'translateY(-50%)',
})

const errorStyles = css({
  fontSize: '$0',
  color: '$red500',
  padding: '0 0 0 $4',

  '.topic__theme--dark &': {
    color: '$red400',
  },
})

const descriptionStyles = css({
  fontSize: '$0',
  color: '$grey600',
  padding: '0 0 0 $4',
})

const inputContainerStyles = css({
  width: '100%',
  position: 'relative',
})

const labelStyles = css({
  position: 'absolute',
  display: 'flex',
  opacity: 0.5,
  top: '50%',
  transform: 'translate3d(0, -52%, 0)',
  left: '$4',
  lineHeight: 1.5,
  color: '$grey500',
  transition: 'all .1s ease-in-out',
  willChange: 'transform, font-size',
  userSelect: 'none',
  zIndex: 3,
  padding: 0,
  pointerEvents: 'none',
  fontSize: '$2',
  borderRadius: '$2',

  '> * + *': {
    marginLeft: '$1',
  },

  '&::before': {
    content: '""',
    position: 'absolute',
    left: '-$1',
    right: '-$1',
    top: 0,
    height: '100%',
    backgroundColor: '$backgroundSecondary',
    padding: '0 $1',
    zIndex: -1,
  },

  variants: {
    hasIcon: {
      true: {
        left: '40px',
      },
    },
  },
})

const requiredLabelStyles = css({
  color: '$red500',
})

const inputStyles = css({
  width: '100%',
  boxSizing: 'border-box',
  position: 'relative',
  overflow: 'hidden',
  padding: '$3 $4',
  transition: 'border-color 90ms ease-out',
  border: '1px solid $grey300',
  fontSize: '$2',
  color: '$foreground',
  borderRadius: '$2 !important',
  backgroundColor: '$backgroundSecondary',

  '&:focus': {
    outline: 0,
    borderColor: '$accent500',
  },

  '&:focus + label, &:not(:placeholder-shown) + label': {
    fontSize: '$1',
    left: '$4',
    transform: 'translate3d(0, -30px, 0)',
    opacity: 1,
    color: '$grey800',
  },

  '&:disabled': {
    opacity: 0.2,
    pointerEvents: 'none',
    backgroundColor: '$grey300',
    borderColor: '$grey600',
    '& + label': {
      opacity: 0.2,
      backgroundColor: 'transparent',
    },
  },

  '.topic__theme--dark &': {},

  '&[aria-invalid="true"]': {
    borderColor: '$red400',
    '&:hover': {
      borderColor: '$red500',
    },
    '&:focus + label, &:not(:placeholder-shown) + label': {
      color: '$red500',
    },
  },

  variants: {
    hasIcon: {
      true: {
        paddingLeft: '40px',
      },
    },
    hasAction: {
      true: {
        paddingRight: '53px',
      }
    }
  },
})
